import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getStoryblokApi } from '@storyblok/react'
import type { ISbStoryData } from '@storyblok/js'
import type { ProductOffer as StoryblokProductOffer } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { env } from '@percent/cause-dashboard/env'
import { isStoryblokPreviewSearch } from '@percent/cause-dashboard/storyblok/helpers/isStoryblokPreviewSearch'
import { mapAllowedCountries } from '@percent/cause-dashboard/storyblok/product-offers/mapStoryblokProductOfferToProductOffer'
import '../storyblokInit'

export const META_PRODUCT_OFFER_STORY_ID = '197652412081386'

export type MetaOfferAllowedCountriesState = {
  allowedCountries: string[] | undefined
  loading: boolean
  error: Error | null
}

export function useMetaOfferAllowedCountries(enabled: boolean): MetaOfferAllowedCountriesState {
  const [searchParams] = useSearchParams()
  const isPreview = isStoryblokPreviewSearch(searchParams)

  const [allowedCountries, setAllowedCountries] = useState<string[] | undefined>(undefined)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!enabled) {
      setAllowedCountries(undefined)
      setError(null)
      setLoading(false)
      return
    }

    const publishedToken = env.NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN
    const previewToken = env.NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN

    if (isPreview && !previewToken) {
      setAllowedCountries(undefined)
      setError(new Error('Storyblok env missing for preview: NX_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    if (!isPreview && !publishedToken) {
      setAllowedCountries(undefined)
      setError(new Error('Storyblok env missing: NX_PUBLIC_STORYBLOK_PUBLIC_ACCESS_TOKEN'))
      setLoading(false)
      return
    }

    const token = isPreview ? previewToken : publishedToken
    const version = isPreview ? 'draft' : 'published'

    let cancelled = false
    setLoading(true)
    setError(null)

    const api = getStoryblokApi()

    api
      .get(`cdn/stories/${META_PRODUCT_OFFER_STORY_ID}`, {
        version,
        token
      })
      .then(res => {
        if (cancelled) return
        const story = res.data?.story as ISbStoryData<StoryblokProductOffer> | undefined
        if (!story?.content || story.content.component !== 'product_offer') {
          setError(new Error('Storyblok: invalid product_offer story response'))
          setAllowedCountries(undefined)
          return
        }
        setAllowedCountries(mapAllowedCountries(story.content.allowedCountries))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err : new Error(String(err)))
        setAllowedCountries(undefined)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, isPreview])

  return { allowedCountries, loading, error }
}
